import { Component } from '@theme/component';

/** Enhances the native mobile localization form without changing its action or return path. */
class GPLanguageSwitch extends Component {
  #abort;
  #observer;
  #timer;
  #drag;
  #busy = false;
  #suppressClickUntil = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#init();
  }
  updatedCallback() {
    super.updatedCallback();
    this.#init();
  }
  #cleanup() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
    this.#drag = null;
  }
  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }
  #init() {
    this.#cleanup();
    this.#busy = false;
    const form = this.querySelector('form');
    if (!form) return;
    const buttons = [...form.querySelectorAll('button[name="language_code"]')];
    if (buttons.length < 2) return;
    const current = buttons.findIndex(button => button.classList.contains('is-active'));
    let selected = Math.max(0, current);
    const place = (index) => {
      if (!buttons[index]) return;
      form.style.setProperty('--gp-language-index', String(index));
    };
    form.classList.add('is-enhanced');
    const select = (index) => {
      if (this.#busy || index === current || !buttons[index]) { place(selected); return; }
      this.#busy = true;
      selected = index;
      for (const [i, button] of buttons.entries()) button.classList.toggle('is-active', i === index);
      place(index);
      this.#timer = setTimeout(() => {
        // Native POST retains Shopify's localized resource routing and country.
        let input = form.querySelector('input[name="language_code"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'language_code';
          form.append(input);
        }
        input.value = buttons[index].value;
        HTMLFormElement.prototype.submit.call(form);
      }, matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 180);
    };
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    form.addEventListener('submit', event => {
      event.preventDefault();
      if (performance.now() < this.#suppressClickUntil) return;
      select(buttons.indexOf(event.submitter));
    }, options);
    form.addEventListener('click', event => {
      if (performance.now() < this.#suppressClickUntil) event.preventDefault();
    }, options);
    form.addEventListener('pointerdown', event => {
      if (!event.isPrimary || event.button !== 0 || this.#busy) return;
      this.#drag = { id: event.pointerId, x: event.clientX, y: event.clientY, active: false };
    }, options);
    form.addEventListener('pointermove', event => {
      const drag = this.#drag;
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
      if (!drag.active) {
        if (Math.abs(dy) > 8 && Math.abs(dy) > Math.abs(dx)) { this.#drag = null; return; }
        if (Math.abs(dx) < 8) return;
        drag.active = true;
        form.setPointerCapture(event.pointerId);
        form.classList.add('is-dragging');
      }
      const rect = form.getBoundingClientRect();
      const segmentWidth = (rect.width - 8) / buttons.length;
      if (segmentWidth <= 0) return;
      const index = Math.max(0, Math.min(buttons.length - 1, (event.clientX - rect.left - 4) / segmentWidth - 0.5));
      form.style.setProperty('--gp-language-index', String(index));
    }, options);
    form.addEventListener('pointerup', event => {
      const drag = this.#drag;
      this.#drag = null;
      form.classList.remove('is-dragging');
      if (!drag?.active || drag.id !== event.pointerId) return;
      this.#suppressClickUntil = performance.now() + 350;
      if (form.hasPointerCapture(event.pointerId)) form.releasePointerCapture(event.pointerId);
      const rect = form.getBoundingClientRect();
      const segmentWidth = (rect.width - 8) / buttons.length;
      const target = Math.max(0, Math.min(buttons.length - 1, Math.floor((event.clientX - rect.left - 4) / segmentWidth)));
      select(target);
    }, options);
    form.addEventListener('pointercancel', () => {
      this.#drag = null;
      form.classList.remove('is-dragging');
      place(selected);
    }, options);
    this.#observer = new ResizeObserver(() => place(selected));
    this.#observer.observe(form);
    place(selected);
  }
}
if (!customElements.get('gp-language-switch')) customElements.define('gp-language-switch', GPLanguageSwitch);
